import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { createInstance, type FeaturevisorInstance } from '@featurevisor/sdk'
import type { DatafileContent } from '@featurevisor/types'
import { featureFlagsDatafileEnv } from '@/config/featureFlags'
import { useAuth } from '@hooks/useAuth'

export interface FeatureFlagsContextValue {
  ready: boolean
  epoch: number
  loadError: Error | null
  isEnabled: (featureKey: string) => boolean
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null)

async function fetchDatafile(url: string, timeoutMs = 14_000): Promise<DatafileContent> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(url, { signal: controller.signal })
    if (!response.ok) {
      throw new Error(`Feature flags: HTTP ${response.status}`)
    }
    return (await response.json()) as DatafileContent
  } finally {
    clearTimeout(timeout)
  }
}

/**
 * Loads Featurevisor datafiles from public/flags (synced from @qwixl/feature-flags-data on install/build).
 * Must sit under `AuthProvider`.
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [contextEpoch, setContextEpoch] = useState(0)
  const [instance, setInstance] = useState<FeaturevisorInstance | null>(null)
  const [ready, setReady] = useState(false)
  const [loadError, setLoadError] = useState<Error | null>(null)

  useEffect(() => {
    let cancelled = false
    const env = featureFlagsDatafileEnv()
    const url = `/flags/${env}/featurevisor-tag-web.json`

    void fetchDatafile(url)
      .then((datafile) => {
        if (cancelled) return
        const nextInstance = createInstance({
          datafile,
          ...(import.meta.env.PROD && {
            datafileUrl: url,
            refreshInterval: 300_000,
          }),
        })
        nextInstance.setContext({ userId: 'anonymous' })
        setInstance(nextInstance)
        setLoadError(null)
        setReady(true)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const err = error instanceof Error ? error : new Error('Feature flags failed to load')
        setInstance(null)
        setReady(false)
        setLoadError(err)
        if (import.meta.env.DEV) {
          console.warn('[FeatureFlags] Failed to load datafile:', err)
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!instance) return
    instance.setContext({ userId: user?.id ?? 'anonymous' })
    setContextEpoch((epoch) => epoch + 1)
  }, [instance, user?.id])

  const isEnabled = useCallback(
    (featureKey: string) => {
      if (!instance) return false
      return instance.isEnabled(featureKey)
    },
    [instance]
  )

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({
      ready,
      epoch: contextEpoch,
      loadError,
      isEnabled,
    }),
    [contextEpoch, isEnabled, loadError, ready]
  )

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  const context = useContext(FeatureFlagsContext)
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider')
  }

  return context
}
